import React, { useState, useEffect } from "react";
import Card from "@amzn/meridian/esm/components/card";
import Column from "@amzn/meridian/esm/components/column";
import Link from "@amzn/meridian/esm/components/link";
import RadioButton from "@amzn/meridian/esm/components/radio-button";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme";
import Toggle from "@amzn/meridian/esm/components/toggle";

import BackButton from "../../components/BackButton";
import InfoCard, { INFO_CARD_VARIANT } from "../../components/InfoCard";
import StartedByStatus from "../../components/StartedByStatus";
import { FEEDBACK_PAGE } from "../../constants";
import { VERBOSITY_LEVELS } from "../../constants";
import { THEME_COLOR } from "../../styles/colors";
import LogManager from "../../utils/logManager";
import { openUrl } from "../../utils/zoomSdkUtils";
import { usePreference } from "../../context/PreferenceContext";
import Loading from "../../components/Loading";

import conciseIcon from "../../images/verbosity_concise.svg";
import moderateIcon from "../../images/verbosity_moderate.svg";
import detailedIcon from "../../images/verbosity_detailed.svg";

const DETAIL_LEVEL_CARDS = [
  { key: VERBOSITY_LEVELS.CONCISE, label: "Concise", icon: conciseIcon },
  { key: VERBOSITY_LEVELS.MODERATE, label: "Moderate", icon: moderateIcon },
  { key: VERBOSITY_LEVELS.DETAILED, label: "Detailed", icon: detailedIcon },
];

const LEARN_MORE_URL = "https://w.amazon.com/bin/view/AmazonMeetings/MeetingSummaries/AiMS/DetailedSummaries/";

/**
 * Settings view with the auto-start toggle, verbosity level selector + FeedbackCard.
 * On mount, fetches current preference. On selection change, auto-saves.
 * The per-meeting "send to all attendees" toggle lives in the footer's settings popover.
 */
const Settings = ({
  isVerboseSummariesEnabled = true,
  isAutoStartEnabled = true,
  onBack,
  startedBy,
  onPreferenceSaveError = () => {},
}) => {
  const { config, isLoaded, updateConfig } = usePreference();
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [autoStartEnabled, setAutoStartEnabled] = useState(false);
  const [autoStartUpdating, setAutoStartUpdating] = useState(false);

  useEffect(() => {
    if (isLoaded) {
      setSelectedLevel(config.verbosityLevel || "concise");
      setAutoStartEnabled(config.autoStart === true);
    }
  }, [isLoaded, config]);

  const handleLevelChange = async (key) => {
    const previousLevel = selectedLevel;
    setSelectedLevel(key);
    try {
      await updateConfig({ verbosityLevel: key });
    } catch (error) {
      LogManager.error("Failed to update preference:", error);
      setSelectedLevel(previousLevel);
      onPreferenceSaveError();
    }
  };

  const handleLearnMoreClick = () => openUrl(LEARN_MORE_URL, "Failed to open Learn more URL:");

  if (isVerboseSummariesEnabled && (!isLoaded || !selectedLevel)) return <Loading />;

  return (
    <Column alignmentHorizontal="center" spacing="450" spacingInset={onBack ? "none" : "450 none none none"}>
      {onBack && (
        <Column spacing="300" spacingInset="300 none none none" width="304px">
          <Row alignmentHorizontal="start">
            <BackButton onClick={onBack} />
          </Row>
          {startedBy && <StartedByStatus startedBy={startedBy} />}
        </Column>
      )}
      {isAutoStartEnabled && (
        <Column spacing="350" width="304px">
          <Column spacing="200">
            <Text type="h100">Meeting insights and summary</Text>
            <Text type="b200" color="secondary">Turn these on for meetings you schedule</Text>
          </Column>
          <Theme tokens={{ toggleBackgroundColorChecked: THEME_COLOR.TEAL_GREEN }}>
            <Toggle
              checked={autoStartEnabled}
              disabled={autoStartUpdating}
              onChange={async () => {
                const newValue = !autoStartEnabled;
                setAutoStartEnabled(newValue);
                setAutoStartUpdating(true);
                try {
                  await updateConfig({ autoStart: newValue });
                  LogManager.metric(`Settings_AutoStart_${newValue ? "Enabled" : "Disabled"}`, 1);
                } catch (error) {
                  LogManager.error("Failed to update auto-start preference:", error);
                  setAutoStartEnabled(!newValue);
                  onPreferenceSaveError();
                } finally {
                  setAutoStartUpdating(false);
                }
              }}
            >
              <Text type="b200">Auto start</Text>
            </Toggle>
          </Theme>
        </Column>
      )}
      {isVerboseSummariesEnabled && (
        <Column spacing="350" width="304px">
          <Text type="h100">Summary detail level</Text>
          <Column spacing="300">
            <Row spacing="300" widths={["96px", "96px", "96px"]}>
              {DETAIL_LEVEL_CARDS.map(({ key, label, icon }) => {
                const isSelected = selectedLevel === key;
                return (
                  <Theme key={key} tokens={{ ...(isSelected && { boxBorderColorOutline: THEME_COLOR.TEAL_GREEN, cardBorderColor: THEME_COLOR.TEAL_GREEN }), radioButtonBackgroundColorSelectedDefault: THEME_COLOR.TEAL_GREEN, radioButtonBackgroundColorSelectedHover: THEME_COLOR.TEAL_GREEN, radioButtonDiameterSmall: 12, radioButtonIndicatorDiameterSmall: 5, radioButtonSpacingHorizontal: 8, radioButtonLabelSmallFontSize: 12, radioButtonLabelSmallLineHeight: "12px" }}>
                    <Card spacingInset="300" onClick={() => handleLevelChange(key)}>
                      <Column spacing="100" alignmentHorizontal="start">
                        <RadioButton checked={isSelected} name="verbosity" value={key} size="small" onChange={() => handleLevelChange(key)}>
                          <b>{label}</b>
                        </RadioButton>
                        <Row alignmentHorizontal="center" alignmentVertical="center" width="100%" height="36px">
                          <img src={icon} alt={label} />
                        </Row>
                      </Column>
                    </Card>
                  </Theme>
                );
              })}
            </Row>
            <Link onClick={handleLearnMoreClick} type="secondary">
              <Theme tokens={{ textColorPrimary: THEME_COLOR.TEAL_GREEN }}>
                <Text color="primary" type="b200"><u>Learn more</u></Text>
              </Theme>
            </Link>
          </Column>
        </Column>
      )}
      <InfoCard variant={INFO_CARD_VARIANT.FEEDBACK} page={FEEDBACK_PAGE.IN_MEETING_FEEDBACK_TAB} />
    </Column>
  );
};

export default Settings;
